<template>
  <div class="col">
    <ShareWithUsersSelector
      v-if="!loading"
      v-model="localSharedWithUsers"
      :access-levels="accessLevels"
      class="q-mt-lg"
      :hide-team-options="hasTeamWorkspaces"
      :organisation-teams="availableTeams"
      :team-name="currentTeamName"
      :theme="theme"
      :users="users"
      @update:model-value="updateSharedWithUsers"
    >
      <template #afterTeamSelect>
        <slot name="afterTeamSelect">
          <span class="soft field-subtext q-mt-md q-pt-xs">
            {{
              $t("stream_settings_shared_tab.user_edit_priviledges_line_1", {
                streamTypeLabel,
              })
            }}
            <br />
            {{
              $t("stream_settings_shared_tab.user_edit_priviledges_line_2", {
                streamTypeLabel,
              })
            }}
          </span>
        </slot>
      </template>
    </ShareWithUsersSelector>
  </div>
</template>

<script>
import { compact, uniqBy } from "lodash-es";
import { storeToRefs } from "pinia";

import ShareWithUsersSelector from "shared/components/selectors/ShareWithUsersSelector.vue";
import useFilteredTeam from "shared/composables/useFilteredTeam";
import useTeamPicker from "shared/composables/useTeamPicker";
import { getAccessLevels, streamTypes } from "shared/constants";
import { labelForStreamType } from "shared/helpers/stream";
import { useUserStore } from "shared/stores/user";

export default {
  name: "StreamSettingsSharedTab",
  components: {
    ShareWithUsersSelector,
  },
  props: {
    streamType: {
      type: Number,
      required: true,
      validator: (value) => Object.values(streamTypes).includes(value),
    },
    theme: {
      type: String,
      default: "default",
    },
    sharedWithUsers: {
      type: Array,
      default: () => [],
    },
    organisationTeamId: {
      type: Number,
      default: null,
    },
    isOrganisationSettings: {
      type: Boolean,
    },
  },
  emits: ["update:sharedWithUsers"],
  setup() {
    const userStore = useUserStore();
    const { currentUser, sortedTeam: team } = storeToRefs(userStore);
    const { getTeam } = userStore;

    const { organisationTeamFromPreference } = useTeamPicker();
    const { teamUsers, organisationUsersWithSelf } = useFilteredTeam();

    return {
      currentUser,
      team,
      getTeam,
      organisationTeamFromPreference,
      teamUsers,
      organisationUsersWithSelf,
    };
  },
  data() {
    return {
      localSharedWithUsers: [],
      loading: true,
      accessLevels: getAccessLevels(),
    };
  },
  computed: {
    streamTypeLabel() {
      return labelForStreamType(this.streamType);
    },
    currentUserAccess() {
      if (this.isOrganisationSettings) {
        return this.sharedWithUsers.find(
          (streamsUser) => streamsUser.id === this.ownerAccess?.id
        );
      }

      return this.sharedWithUsers.find(
        (streamsUser) => streamsUser.id === this.currentUser.id
      );
    },
    ownerAccess() {
      return this.sharedWithUsers.find(
        (access) => access.access_level === "owner"
      );
    },
    restrictContentSharingToTeam() {
      return (
        this.$features.has("has_restricted_content_sharing_to_team") &&
        !this.$permissions.has("can_share_with_all_organisation_users")
      );
    },
    availableTeams() {
      let availableTeams = [];

      if (this.hasTeamWorkspaces) return availableTeams;

      if (this.restrictContentSharingToTeam) {
        availableTeams = this.currentUser.organisation_teams.map(
          (team) => team
        );
      } else {
        availableTeams = compact(
          uniqBy(
            this.team
              .filter(
                (user) =>
                  user.organisation_teams.filter((team) => team.primary).length
              )
              .flatMap((user) =>
                user.organisation_teams.filter((team) => team.primary)
              ),
            "id"
          )
        );
      }

      return availableTeams;
    },
    users() {
      const users = this.isOrganisationSettings
        ? this.organisationUsersWithSelf(this.organisationTeamId)
        : this.teamUsers;

      return users.filter(
        (user) =>
          ![this.ownerAccess?.id, this.currentUserAccess?.id].includes(user.id)
      );
    },
    hasTeamWorkspaces() {
      return this.$features.has("has_team_workspaces");
    },
    currentTeamName() {
      return this.organisationTeamFromPreference?.name;
    },
  },
  async mounted() {
    this.localSharedWithUsers = this.sharedWithUsers
      .filter(
        (streamsUser) =>
          ![this.ownerAccess?.id, this.currentUserAccess?.id].includes(
            streamsUser.id
          )
      )
      .map((streamsUser) => ({
        id: streamsUser.id,
        accessLevel:
          streamsUser.access_level ||
          (streamsUser.can_edit ? "read_write" : "read_only"),
      }));

    if (!this.team.length) await this.getTeam();
    this.loading = false;
  },
  methods: {
    updateSharedWithUsers(newSharedWithUsers) {
      const updatedSharedWithUsers = [];

      newSharedWithUsers.forEach((sharedWithUser) => {
        if (!sharedWithUser.accessLevel && !sharedWithUser.removed) return;

        updatedSharedWithUsers.push({
          updated: true,
          id: sharedWithUser.id,
          name: sharedWithUser.name,
          label: sharedWithUser.label,
          removed: sharedWithUser.removed,
          access_level: sharedWithUser.accessLevel,
          can_edit: ["owner", "read_write"].includes(
            sharedWithUser.accessLevel
          ),
        });
      });

      // move removed users to the end of the array
      updatedSharedWithUsers.sort((user1, user2) => {
        if (user1.removed && !user2.removed) {
          return 1;
        }

        if (!user1.removed && user2.removed) {
          return -1;
        }

        return 0;
      });

      if (this.currentUserAccess)
        updatedSharedWithUsers.push(this.currentUserAccess);

      if (
        this.ownerAccess &&
        this.ownerAccess.id !== this.currentUserAccess?.id
      ) {
        updatedSharedWithUsers.push(this.ownerAccess);
      }

      this.$emit("update:sharedWithUsers", updatedSharedWithUsers);
    },
  },
};
</script>
